import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { AuthProvider } from "./hooks/use-auth";
import { StrictMode, Suspense, useState, useEffect, Component, ReactNode } from "react";
import { Loader2 } from "lucide-react";

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="flex flex-col items-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="mt-4 text-sm text-muted-foreground">Loading application...</p>
      </div>
    </div>
  );
}

// Simple ErrorBoundary component using plain type definition
class SimpleErrorBoundary extends Component<{children: ReactNode}> {
  state = { 
    hasError: false, 
    errorMessage: "" 
  };
  
  static getDerivedStateFromError(error: unknown) {
    return { 
      hasError: true, 
      errorMessage: error instanceof Error ? error.message : "An unexpected error occurred"
    };
  }
  
  componentDidCatch(error: unknown, errorInfo: unknown) {
    console.error("Application error:", error, errorInfo);
  }
  
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen p-4 bg-background">
          <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-md text-center">
            <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">{this.state.errorMessage}</p>
            <p className="mb-4">
              <a href="/deploy-debug.html" className="text-blue-500 underline">
                Run Deployment Diagnostics
              </a>
            </p>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function NetlifyEnvironmentChecker() {
  const [showNetlifyDebug, setShowNetlifyDebug] = useState(false);
  
  useEffect(() => {
    // Check for environment variables and log for debugging
    console.log('Environment:', {
      apiUrl: import.meta.env.VITE_API_URL || "Not set (using default)",
      mode: import.meta.env.MODE,
      isNetlify: window.location.hostname.includes('netlify.app'),
    });
    
    // Only show extra debug info in Netlify environment
    if (window.location.hostname.includes('netlify.app')) {
      setShowNetlifyDebug(true);
    }
  }, []);
  
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SimpleErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <App />
            {showNetlifyDebug && (
              <div 
                style={{
                  position: 'fixed',
                  bottom: '10px',
                  right: '10px',
                  padding: '5px 10px',
                  background: 'rgba(0,0,0,0.1)',
                  borderRadius: '4px',
                  fontSize: '11px',
                  color: '#666',
                }}
              >
                <a href="/deploy-debug.html" style={{color: 'inherit'}}>
                  Debug Mode
                </a>
              </div>
            )}
          </Suspense>
        </SimpleErrorBoundary>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// Create root and render application
const rootElement = document.getElementById("root");
if (rootElement) {
  createRoot(rootElement).render(
    <StrictMode>
      <NetlifyEnvironmentChecker />
    </StrictMode>
  );
} else {
  console.error("Failed to find the root element");
}
